import { NgModule, inject } from '@angular/core';

import { BrowserModule } from '@angular/platform-browser';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRouteSnapshot, RouterModule, RouterStateSnapshot } from '@angular/router';

import { AlertComponent } from './components/alert/alert.component';
import { BannerCardComponent } from './components/banner/banner-card.component';
import { BannerLocaleComponent } from './components/banner/banner-locale.component';
import { BannerComponent } from './components/banner/banner.component';
import { ChallengeCardComponent } from './components/challenge/challenge-card.component';
import { ChallengeDetailComponent } from './components/challenge/challenge-detail.component';
import { ChallengeComponent } from './components/challenge/challenge.component';
import { CommunityCommentCardComponent } from './components/community/community-comment-card.component';
import { CommunityCommentComponent } from './components/community/community-comment.component';
import { CommunityMessageCardComponent } from './components/community/community-message-card.component';
import { CommunityMessageComponent } from './components/community/community-message.component';
import { CommunityNewCommentComponent } from './components/community/community-new-comment.component';
import { CommunityModalComponent } from './components/community/community-modal.component';
import { CommunityComponent } from './components/community/community.component';
import { DeviceComponent } from './components/device/device.component';
import { FirmwareV1Component } from './components/firmware/firmware-v1.component';
import { FirmwareV2MyScaleComponent } from './components/firmware/firmware-v2-myscale.component';
import { FirmwareV2Component } from './components/firmware/firmware-v2.component';
import { FirmwareComponent } from './components/firmware/firmware.component';
import { HomeComponent } from './components/home/home.component';
import { LoginComponent } from './components/login/login.component';
import { NavigationComponent } from './components/navigation/navigation.component';
import { NotificationComponent } from './components/notification/notification.component';
import { NotFoundComponent } from './components/not-found/not-found.component';
import { ProgressIndicatorComponent } from './components/progress-indicator/progress-indicator.component';
import { RouterLoginComponent } from './components/router-login/router-login.component';
import { RouterNavigationComponent } from './components/router-navigation/router-navigation.component';
import { SessionErrorComponent } from './components/session-error/session-error.component';
import { UserSearchComponent } from './components/user/user-search.component';
import { UserV1Component } from './components/user/user-v1.component';
import { UserV2Component } from './components/user/user-v2.component';
import { UserComponent } from './components/user/user.component';
import { WatchfaceCardComponent } from './components/watchface/watchface-card.component';
import { WatchfaceSecretComponent } from './components/watchface/watchface-secret.component';
import { WatchfaceComponent } from './components/watchface/watchface.component';

import { TokenGuard } from './guards/token.guard';

import { AuthorizationHeaderInterceptor } from './interceptors/authorization-header.interceptor';

import { AlertService } from './services/alert.service';
import { BannerService } from './services/banner.service';
import { ChallengeService } from './services/challenge.service';
import { CommunityService } from './services/community.service';
import { FirmwareService } from './services/firmware.service';
import { LoginService } from './services/login.service';
import { PairingService } from './services/pairing.service';
import { PersonalService } from './services/personal.service';
import { ProgressIndicatorService } from './services/progress-indicator.service';
import { TrackingService } from './services/tracking.service';
import { UserService } from './services/user.service';
import { WatchfaceService } from './services/watchface.service';

import { AppComponent } from './app.component';

@NgModule({
    bootstrap: [
        AppComponent
    ],
    declarations: [
        AlertComponent,
        BannerCardComponent,
        BannerLocaleComponent,
        BannerComponent,
        ChallengeCardComponent,
        ChallengeDetailComponent,
        ChallengeComponent,
        CommunityCommentCardComponent,
        CommunityCommentComponent,
        CommunityMessageCardComponent,
        CommunityMessageComponent,
        CommunityNewCommentComponent,
        CommunityModalComponent,
        CommunityComponent,
        DeviceComponent,
        FirmwareV1Component,
        FirmwareV2MyScaleComponent,
        FirmwareV2Component,
        FirmwareComponent,
        HomeComponent,
        LoginComponent,
        NavigationComponent,
        NotificationComponent,
        NotFoundComponent,
        ProgressIndicatorComponent,
        RouterLoginComponent,
        RouterNavigationComponent,
        SessionErrorComponent,
        UserSearchComponent,
        UserV1Component,
        UserV2Component,
        UserComponent,
        WatchfaceCardComponent,
        WatchfaceSecretComponent,
        WatchfaceComponent,
        AppComponent
    ],
    imports: [
        BrowserModule,
        HttpClientModule,
        FormsModule,
        ReactiveFormsModule,
        RouterModule.forRoot([
            {
                path: '',
                component: RouterNavigationComponent,
                canActivateChild: [(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(TokenGuard).canActivateChild(childRoute, state)],
                children: [
                    { path: '', component: HomeComponent },
                    { path: 'banners', component: BannerComponent },
                    { path: 'challenges', component: ChallengeComponent },
                    { path: 'community', redirectTo: '/community/messages', pathMatch: 'full' },
                    { path: 'community/messages', component: CommunityComponent },
                    { path: 'community/messages/:messageId', component: CommunityMessageComponent },
                    { path: 'community/comments/:commentId', component: CommunityCommentComponent },
                    { path: 'devices', component: DeviceComponent },
                    { path: 'devices/:serialNumber', component: DeviceComponent },
                    { path: 'firmwares', component: FirmwareComponent },
                    { path: 'notifications', component: NotificationComponent },
                    { path: 'users', component: UserSearchComponent },
                    { path: 'users/:accountId', component: UserComponent },
                    { path: 'watchfaces', component: WatchfaceComponent },
                    { path: 'watchfaces/:watchfaceId', component: WatchfaceComponent }
                ]
            },
            {
                path: '',
                component: RouterLoginComponent,
                children: [
                    { path: 'login', component: LoginComponent },
                    { path: 'watchfaces/:watchfaceId/approve/:secret', component: WatchfaceSecretComponent, data: { approve: true } },
                    { path: 'watchfaces/:watchfaceId/reject/:secret', component: WatchfaceSecretComponent, data: { reject: true } },
                    { path: '**', component: NotFoundComponent }
                ]
            }
        ])
    ],
    providers: [
        TokenGuard,
        { provide: HTTP_INTERCEPTORS, useClass: AuthorizationHeaderInterceptor, multi: true },
        AlertService,
        BannerService,
        ChallengeService,
        CommunityService,
        FirmwareService,
        LoginService,
        PairingService,
        PersonalService,
        ProgressIndicatorService,
        TrackingService,
        UserService,
        WatchfaceService
    ]
})
export class AppModule { }
